import { useState, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function Analytics() {
  const [overview, setOverview] = useState(null);
  const [articleStats, setArticleStats] = useState([]);
  const [regionStats, setRegionStats] = useState([]);
  const [dauData, setDauData] = useState([]);
  const [adStats, setAdStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/analytics/overview'),
      api.get('/admin/analytics/articles?limit=10'),
      api.get('/admin/analytics/regions'),
      api.get('/admin/analytics/daily?days=14'),
      api.get('/admin/analytics/ads'),
    ]).then(([ov, art, reg, dau, ads]) => {
      setOverview(ov.data.data);
      setArticleStats(art.data.data);
      setRegionStats(reg.data.data);
      setDauData(dau.data.data);
      setAdStats(ads.data.data);
    }).catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page-loader">Loading...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h2>Analytics</h2>
      </div>

      {/* Overview */}
      <div className="stats-grid">
        {[
          { label: 'Total Users', value: overview?.totalUsers || 0 },
          { label: 'Guest Devices', value: overview?.totalGuests || 0 },
          { label: 'Articles Today', value: overview?.articlesToday || 0 },
          { label: 'Reads Today', value: overview?.readsToday || 0 },
        ].map((s, i) => (
          <div key={i} className="stat-card">
            <div className="stat-info">
              <h3>{s.value}</h3>
              <p>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* DAU Chart */}
      <div className="card chart-card">
        <h3>Daily Active Users (14 days)</h3>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={dauData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0d0d0" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Line type="monotone" dataKey="active_users" stroke="#DC2626" strokeWidth={2} dot={{ fill: '#DC2626' }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Region Chart */}
      <div className="card chart-card">
        <h3>Reads by Region</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={regionStats}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0d0d0" />
            <XAxis dataKey="region" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="read_count" fill="#DC2626" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top Articles */}
      <div className="card">
        <h3 className="card-title">Top Articles (by views)</h3>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Views</th>
                <th>Likes</th>
                <th className="hide-mobile">Comments</th>
                <th className="hide-mobile">Category</th>
              </tr>
            </thead>
            <tbody>
              {articleStats.map(a => (
                <tr key={a.id}>
                  <td className="title-cell">{a.title}</td>
                  <td>{a.view_count}</td>
                  <td>{a.like_count}</td>
                  <td className="hide-mobile">{a.comment_count}</td>
                  <td className="hide-mobile"><span className="badge badge-category">{a.category?.replace('_', ' ')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ad Performance */}
      <div className="card">
        <h3 className="card-title">Ad Performance</h3>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Ad</th>
                <th>Type</th>
                <th>Impressions</th>
                <th>Taps</th>
                <th>CTR</th>
              </tr>
            </thead>
            <tbody>
              {adStats.map(ad => (
                <tr key={ad.id}>
                  <td>{ad.title || `Ad #${ad.id}`}</td>
                  <td><span className="badge">{ad.ad_type === 'sponsored_card' ? 'Sponsored' : 'Fullscreen'}</span></td>
                  <td>{ad.impression_count}</td>
                  <td>{ad.tap_count}</td>
                  <td>{ad.ctr}%</td>
                </tr>
              ))}
              {adStats.length === 0 && <tr><td colSpan="5" className="empty-row">No ads</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
